import { useId, type ReactNode } from "react";
import { useTranslate } from "ra-core";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { EXPEDIENTE_FIELD_LABELS } from "../../providers/commons/contactExpediente";
import type { ContactExpedienteInput } from "../../types";
import type { ExpedienteFieldMessages } from "./expedienteFormRules";

/** Shared contract of every field group of the Expediente form. */
export type ExpedienteFieldGroupProps = {
  values: ContactExpedienteInput;
  /** Errors and warnings already merged, one Spanish message per field. */
  messages: ExpedienteFieldMessages;
  onChange: <K extends keyof ContactExpedienteInput>(
    field: K,
    value: ContactExpedienteInput[K] | null,
  ) => void;
};

const CONTROL_CLASS =
  "h-11 w-full rounded-md border border-input bg-background px-3 text-[15px] tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-10";

type FieldShellProps = {
  id: string;
  label: string;
  message?: string;
  children: ReactNode;
  className?: string;
};

const FieldShell = ({
  id,
  label,
  message,
  children,
  className,
}: FieldShellProps) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <Label htmlFor={id} className="text-[15px]">
      {label}
    </Label>
    {children}
    {message ? (
      <p
        id={`${id}-message`}
        role="alert"
        className="text-sm text-brand-red-text"
      >
        {message}
      </p>
    ) : null}
  </div>
);

type TextInputProps = ExpedienteFieldGroupProps & {
  field: keyof ContactExpedienteInput;
  type?: "text" | "date";
  maxLength?: number;
  className?: string;
};

/** A labelled text (or date) input bound to one expediente field; blank is saved as empty. */
export const ExpedienteTextInput = ({
  field,
  values,
  messages,
  onChange,
  type = "text",
  maxLength,
  className,
}: TextInputProps) => {
  const id = useId();
  const message = messages[field];
  return (
    <FieldShell
      id={id}
      label={EXPEDIENTE_FIELD_LABELS[field]}
      message={message}
      className={className}
    >
      <input
        id={id}
        type={type}
        autoComplete="off"
        maxLength={maxLength}
        className={CONTROL_CLASS}
        value={(values[field] as string | null | undefined) ?? ""}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? `${id}-message` : undefined}
        onChange={(event) =>
          onChange(
            field,
            event.target.value as ContactExpedienteInput[typeof field],
          )
        }
      />
    </FieldShell>
  );
};

type SelectInputProps = ExpedienteFieldGroupProps & {
  field: keyof ContactExpedienteInput;
  /** value -> Spanish label. */
  options: Record<string, string>;
  className?: string;
};

/** A labelled native select bound to one expediente field (empty option clears it). */
export const ExpedienteSelectInput = ({
  field,
  options,
  values,
  messages,
  onChange,
  className,
}: SelectInputProps) => {
  const id = useId();
  const translate = useTranslate();
  const message = messages[field];
  return (
    <FieldShell
      id={id}
      label={EXPEDIENTE_FIELD_LABELS[field]}
      message={message}
      className={className}
    >
      <select
        id={id}
        className={CONTROL_CLASS}
        value={(values[field] as string | null | undefined) ?? ""}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? `${id}-message` : undefined}
        onChange={(event) =>
          onChange(
            field,
            (event.target.value ||
              null) as ContactExpedienteInput[typeof field],
          )
        }
      >
        <option value="">{translate("crm.contact_expediente.choose")}</option>
        {Object.entries(options).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
};

type RadioChoice<V extends string> = { value: V; label: string };

/** A group of native radios (44 px targets) for a small closed choice. */
export const ExpedienteRadioGroup = <V extends string>({
  legend,
  name,
  choices,
  selected,
  message,
  onSelect,
}: {
  legend: string;
  name: string;
  choices: RadioChoice<V>[];
  selected: V | null;
  message?: string;
  onSelect: (value: V) => void;
}) => (
  <fieldset className="flex flex-col gap-1.5">
    <legend className="mb-1.5 text-[15px] font-medium">{legend}</legend>
    <div className="flex flex-wrap gap-2">
      {choices.map((choice) => (
        <label
          key={choice.value}
          className={cn(
            "flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-4 text-[15px] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
            selected === choice.value
              ? "border-primary bg-primary/5 font-medium"
              : "border-input bg-background",
          )}
        >
          <input
            type="radio"
            name={name}
            value={choice.value}
            checked={selected === choice.value}
            onChange={() => onSelect(choice.value)}
          />
          {choice.label}
        </label>
      ))}
    </div>
    {message ? (
      <p role="alert" className="text-sm text-brand-red-text">
        {message}
      </p>
    ) : null}
  </fieldset>
);
